/**
 * Rebuilds the reasoning trace of a past conversation.
 *
 * The runtime already stores every AG-UI event it emitted, so reopening a
 * conversation does not need a second copy of the trace: the same events are
 * read back and mapped to the same steps. Routing that through the very
 * dispatch() the live stream uses is the point — a replayed step and a live
 * step cannot drift apart, because there is only one mapping.
 */
import {
  dispatch,
  type AgUiEvent,
  type DashboardComposedEvent,
} from "./agui";
import type { AnswerContext } from "./answerContext";
import type { StoredRunEvent } from "./api";
import type { DevTrace } from "../types";

/**
 * The trace step for an answer's context, live or replayed.
 *
 * Shared so the reasoning panel says the same thing about a turn whether it
 * watched the turn happen or read it back from history.
 */
export function answerContextStep(
  context: AnswerContext,
): Pick<DevTrace, "step" | "actor" | "level" | "summary" | "output"> {
  return {
    step: "answer_context",
    actor: "Conversational Analyst",
    level: "info",
    summary: `${context.summary} ${context.interpretation}`.trim(),
    output: {
      frameId: context.frameId,
      relation: context.relation,
      changes: context.changes.map((change) => change.field),
    },
  };
}

export function tracesFromEvents(events: StoredRunEvent[]): DevTrace[] {
  const traces: DevTrace[] = [];

  for (const event of events) {
    // The runtime's own (run, sequence) identity, so replaying a conversation
    // twice reconciles onto the same steps rather than doubling them.
    const base = {
      id: `${event.runId}:${event.sequence}`,
      runId: event.runId,
      timestamp: event.createdAt,
    };

    // RUN_STARTED has no live handler: the page records the opening step
    // itself, before the stream exists. A replay has no page to do that, so
    // the step is reconstructed here and history reads like a live turn.
    if (event.eventType === "RUN_STARTED") {
      traces.push({
        ...base,
        step: "agent_turn_started",
        actor: "AI Analyst",
        level: "info",
        summary: "Sent the question to the governed agent network.",
      });
      continue;
    }

    dispatch(event.payload as AgUiEvent, {
      onTrace: (summary, actor) =>
        traces.push({
          ...base,
          step: "agent_activity",
          actor,
          level: "success",
          summary,
        }),
      onStep: (step) => {
        const detail = step.detail ?? {};
        traces.push({
          ...base,
          step: step.phase,
          actor: step.actor,
          level:
            step.status === "error"
              ? "error"
              : step.status === "success"
                ? "success"
                : "info",
          summary: step.summary,
          phase: step.phase,
          layer: step.layer,
          service: step.service,
          tool: step.tool,
          code: typeof detail.code === "string" ? detail.code : undefined,
          detail,
          durationMs:
            typeof detail.durationMs === "number"
              ? detail.durationMs
              : undefined,
        });
      },
      onDashboardComposed: (composed) =>
        traces.push({
          ...base,
          step: "dashboard_composed",
          actor: "Dashboard Composer",
          level: composed.saved ? "success" : "warning",
          summary: composed.saved
            ? `Composed "${composed.title}" with ` +
              `${composed.spec.panels.length} panels.`
            : `Composed "${composed.title}" but it could not be saved.`,
          output: {
            dashboardId: composed.dashboardId,
            panels: composed.spec.panels.length,
            saved: composed.saved,
          },
        }),
      onAnswerContext: (context) =>
        traces.push({ ...base, ...answerContextStep(context) }),
      onError: (code, message, details) =>
        traces.push({
          ...base,
          step: "agent_error",
          actor: "AI Analyst",
          level: "error",
          summary: `The agent reported ${code}.`,
          code,
          output: { code, message },
          detail: details,
        }),
    });
  }

  return traces;
}

/** The runs a replay covers, so the panel can show a whole conversation. */
export function runIdsOf(events: StoredRunEvent[]): string[] {
  return [...new Set(events.map((event) => event.runId))];
}

/**
 * The dashboards a conversation produced, oldest first.
 *
 * Read back through the same dispatch() the live stream uses, so a replayed
 * dashboard and a live one cannot drift apart. Reopening a conversation used to
 * lose its charts entirely: they were page state, discarded on open.
 */
export function dashboardsFromEvents(
  events: StoredRunEvent[],
): DashboardComposedEvent[] {
  const composed: DashboardComposedEvent[] = [];

  for (const event of events) {
    dispatch(event.payload as AgUiEvent, {
      onDashboardComposed: (dashboard) => composed.push(dashboard),
    });
  }

  // A dashboard that was never saved cannot be reopened, so replaying it would
  // offer the user a workspace entry that does not exist.
  return composed.filter((dashboard) => dashboard.saved && dashboard.dashboardId);
}
